/**
 * O que faz: Mantém resumo e atendimento independentes, com cache isolado por sessão/clínica.
 * Onde usa: Dashboard normal e administrativo, inclusive detalhe de clínica.
 * Conexões: AuthContext, React Query, dashboardResumoService e contratos de período.
 * Tabelas: Duas RPCs somente leitura; nenhuma paginação integral de leads ou chat.
 * Data: 31/08/2026.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { serializarPeriodoDashboard, type ContextoDashboard } from '@/features/dashboard/data/dashboardContracts';
import { adaptarAtendimentoDashboard, adaptarResumoDashboard, buscarAtendimentoDashboard, buscarResumoDashboard } from '@/services/dashboardResumoService';

export const chaveContextoDashboard = (contexto?: ContextoDashboard) =>
  [contexto?.usuarioId, contexto?.clinicaId, contexto?.modo] as const;

export function useDashboardConsultas(clinicaId: string | null | undefined, inicio: Date | null, fim: Date | null,
  modo: ContextoDashboard['modo'], atendimentoHabilitado = true) {
  const { user } = useAuth();
  const periodo = serializarPeriodoDashboard(inicio, fim);
  const contexto = useMemo<ContextoDashboard | undefined>(() => user?.id && clinicaId ? {
    usuarioId: user.id, clinicaId, modo, periodo: { inicio: periodo.inicio, fim: periodo.fim },
  } : undefined, [user?.id, clinicaId, modo, periodo.inicio, periodo.fim]);
  const identidade = chaveContextoDashboard(contexto);
  const resumo = useQuery({
    queryKey: ['dashboard-v1', ...identidade, 'resumo', periodo],
    queryFn: ({ signal }) => buscarResumoDashboard(clinicaId!, periodo, signal),
    enabled: !!contexto, staleTime: 60_000, gcTime: 10 * 60_000, retry: 1, refetchOnWindowFocus: false,
    // Reutilizar outro período da mesma identidade é seguro; outra clínica/sessão nunca vira placeholder.
    placeholderData: (anterior, consulta) => consulta?.queryKey.slice(1,4).every((valor,index) => valor === identidade[index]) ? anterior : undefined,
  });
  const atendimento = useQuery({
    queryKey: ['dashboard-v1', ...identidade, 'atendimento', periodo],
    queryFn: ({ signal }) => buscarAtendimentoDashboard(clinicaId!, periodo, signal),
    enabled: !!contexto && atendimentoHabilitado, staleTime: 60_000, gcTime: 10 * 60_000, retry: 1, refetchOnWindowFocus: false,
  });
  const dados = useMemo(() => {
    if (!contexto || !resumo.data) return null;
    // Atendimento do novo período não deve ser misturado ao resumo anterior durante placeholder.
    const mesmoPeriodo = atendimento.data?.periodo.inicio === resumo.data.periodo.inicio
      && atendimento.data?.periodo.fim === resumo.data.periodo.fim;
    return { ...adaptarResumoDashboard(resumo.data), ...(mesmoPeriodo && atendimento.data ? adaptarAtendimentoDashboard(atendimento.data) : {}) };
  }, [contexto, resumo.data, atendimento.data]);
  return { contexto, resumo, atendimento, dados };
}
